// Key must be in capitalised Snake case to match the error codes returned by the API
export enum ApiErrorCode {
  DUPLICATE_EMAIL,
  DUPLICATE_FIELD,
  UNKNOWN_ERROR,
  ENTITY_HAS_DEPENDENCIES,
  SPREADSHEET_TOO_LARGE,
  UNSUPPORTED_FILE_EXTENSION,
  CLIENT_ALREADY_IN_FAMILY,
}

const GLOBALLY_HANDLED_CODES = new Set([
  ApiErrorCode.ENTITY_HAS_DEPENDENCIES,
  ApiErrorCode.SPREADSHEET_TOO_LARGE,
  ApiErrorCode.UNSUPPORTED_FILE_EXTENSION,
]);

export class ApiError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
    public readonly errorId?: string,
    public readonly errorType?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  private static isKnownErrorCode(code?: string): code is keyof typeof ApiErrorCode {
    return code != null && code in ApiErrorCode;
  }

  static isGloballyHandledError(error: unknown): error is ApiError {
    return error instanceof ApiError && GLOBALLY_HANDLED_CODES.has(error.code);
  }

  static isApiError(code: ApiErrorCode, error: unknown): error is ApiError {
    if (error instanceof ApiError && ApiErrorCode[error.code] === ApiErrorCode[code]) {
      return true;
    }

    return false;
  }

  static async fromApiResponse(response: Response) {
    const data = await response.json();

    // Extract backend error correlation fields
    const errorId = data.error_id;
    const errorType = data.error_type;

    let errorMessage: string;
    let errorCode: string | undefined;

    if (typeof data.detail === 'string') {
      errorMessage = data.detail;
      errorCode = undefined;
    } else if (typeof data.detail === 'object' && data.detail !== null) {
      errorMessage = data.detail.message || 'An error occurred';
      errorCode = data.detail.error_code;
    } else {
      errorMessage = data.message || 'An error occurred';
      errorCode = undefined;
    }

    const code = ApiErrorCode[errorCode as keyof typeof ApiErrorCode] ?? ApiErrorCode.UNKNOWN_ERROR;

    if (this.isKnownErrorCode(errorCode)) {
      if (code === ApiErrorCode.DUPLICATE_FIELD) {
        return new AfternoonDuplicateFieldApiError(
          errorMessage,
          response.status,
          data.detail?.field || 'unknown',
          errorId,
          errorType,
        );
      }
      return new ApiError(
        ApiErrorCode[errorCode],
        errorMessage,
        response.status,
        errorId,
        errorType,
      );
    }

    return new ApiError(code, errorMessage, response.status, errorId, errorType);
  }
}

export class AfternoonDuplicateFieldApiError extends ApiError {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field: string,
    errorId?: string,
    errorType?: string,
  ) {
    super(ApiErrorCode.DUPLICATE_FIELD, message, status, errorId, errorType);
  }

  static isDuplicateFieldError(error: unknown): error is AfternoonDuplicateFieldApiError {
    return (
      this.isApiError(ApiErrorCode.DUPLICATE_FIELD, error) &&
      error instanceof AfternoonDuplicateFieldApiError
    );
  }
}
